import React, { useRef } from "react";

import classNames from "classnames";
import { useDrop } from "react-dnd";
import { useTranslation } from "react-i18next";

import SortableItem from "./SortableItem";

import type { JSX } from "react";

import type { ItemDisposition, ZoneDisposition } from "./Disposition";
import type { ItemDragType } from "../../../../constants/dragTypes";

interface AvailableItemListProps {
  itemList: ItemDisposition[];
  onDrop: ({
    itemId,
    targetZone,
  }: {
    itemId: string;
    targetZone: ZoneDisposition;
  }) => void;
  acceptItemType: ItemDragType;
}

function AvailableItemList({
  itemList,
  onDrop,
  acceptItemType,
}: AvailableItemListProps): JSX.Element {
  const { t } = useTranslation("editor-platform");

  const dropRef = useRef<HTMLDivElement>(null);

  const [{ isOver }, dropConnector] = useDrop(
    () => ({
      accept: acceptItemType,
      hover: (item: { id: string }): void => {
        onDrop({ itemId: item.id, targetZone: "available" });
      },
      collect: (monitor) => ({
        isOver: !!monitor.isOver(),
      }),
    }),
    [onDrop, acceptItemType],
  );

  dropConnector(dropRef);

  const dropzoneClassNames = classNames(
    "min-h-[80px] border p-[0.625rem] transition-all duration-200 flex flex-nowrap items-center gap-[0.3125rem] overflow-x-auto rounded-[0.3125rem] relative",
    {
      "border-input bg-primary/[0.02]": isOver,
      "border-border bg-muted": !isOver,
    },
  );

  return (
    <div className="flex w-full flex-1 flex-col gap-[0.3125rem] overflow-hidden">
      <div className="text-muted-foreground font-semibold">
        {t("itemsAvailable")}
      </div>
      <div ref={dropRef} className={dropzoneClassNames}>
        {itemList.map((item) => (
          <SortableItem
            key={item.id}
            acceptItemType={acceptItemType}
            item={item}
            targetZone="available"
          />
        ))}
        {itemList.length === 0 && (
          <div className="text-muted-foreground flex size-full items-center justify-center italic">
            {t("noItemsAvailable")}
          </div>
        )}
      </div>
    </div>
  );
}

export default AvailableItemList;
